import React, { useState } from 'react';
import { FaArrowLeft, FaUpload, FaCheckCircle, FaRegClock, FaCopy, FaQrcode, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const BankTransferPayment = ({ userId, amount, onBack, onSuccess }) => {
    const [uploading, setUploading] = useState(false);
    const [paymentData, setPaymentData] = useState({
        transactionId: '',
        bankName: '',
        accountName: '',
        accountNumber: '',
        paymentDate: '',
        notes: ''
    });
    const [proofImage, setProofImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [fileError, setFileError] = useState('');

    // Bank Account Details (Update with your actual bank details)
    const bankDetails = {
        bankName: 'NMB Bank Babarmahal Kathmandu Nepal.',
        accountName: 'BIHEBARI MARRIAGE BUREAU PVT LTD',
        accountNumber: '0010116311400015',
        accountType: 'Current Account',
        branch: 'Babarmahal, Kathmandu',
        swiftCode: 'NMBBNPKA',
        qrCodeUrl: 'https://biheybari.com/qr.png'
    };

    // Security: Allowed file types
    const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB (reduced from 5MB for security)

    // Security: Validate file extension
    const isValidFileExtension = (filename) => {
        const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
        return ALLOWED_EXTENSIONS.includes(ext);
    };

    // Security: Sanitize filename (remove potentially dangerous characters)
    const sanitizeFilename = (filename) => {
        return filename.replace(/[^a-zA-Z0-9.-]/g, '_');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Security: Sanitize input to prevent XSS
        const sanitizedValue = value.replace(/[<>]/g, '');
        setPaymentData(prev => ({ ...prev, [name]: sanitizedValue }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFileError('');
        
        if (file) {
            // Security: Check file extension
            if (!isValidFileExtension(file.name)) {
                setFileError(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
                toast.error(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
                return;
            }

            // Security: Check MIME type
            if (!ALLOWED_FILE_TYPES.includes(file.type)) {
                setFileError('Invalid file format. Please upload JPEG, PNG, or WEBP images only.');
                toast.error('Invalid file format. Please upload JPEG, PNG, or WEBP images only.');
                return;
            }

            // Security: Check file size
            if (file.size > MAX_FILE_SIZE) {
                setFileError(`File size should be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`);
                toast.error(`File size should be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`);
                return;
            }

            // Security: Check if file is empty
            if (file.size === 0) {
                setFileError('File is empty');
                toast.error('File is empty');
                return;
            }

            // Security: Scan for potential malicious content (basic check)
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target.result;
                // Check for HTML/JS injection in image (basic)
                if (content.includes('<script') || content.includes('<?php')) {
                    setFileError('File contains suspicious content');
                    toast.error('File contains suspicious content');
                    return;
                }
                
                setProofImage(file);
                setImagePreview(URL.createObjectURL(file));
            };
            reader.readAsText(file);
            reader.onerror = () => {
                // If can't read as text, it's likely a binary image - that's fine
                setProofImage(file);
                setImagePreview(URL.createObjectURL(file));
            };
        }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        toast.success(`${field} copied to clipboard!`);
    };

    const downloadQRCode = () => {
        if (bankDetails.qrCodeUrl && bankDetails.qrCodeUrl !== 'https://example.com/qr-code.png') {
            window.open(bankDetails.qrCodeUrl, '_blank');
        } else {
            toast.info('QR code not available. Please use the bank details below.');
        }
    };

    // Security: Validate transaction ID format
    const isValidTransactionId = (id) => {
        // Allow alphanumeric, hyphens, underscores (basic validation)
        const regex = /^[a-zA-Z0-9\-_]+$/;
        return regex.test(id);
    };

    const handleSubmit = async () => {
        // Clear previous errors
        setFileError('');

        // Validate transaction ID
        if (!paymentData.transactionId) {
            toast.error('Please enter Transaction ID');
            return;
        }

        // Security: Validate transaction ID format
        if (!isValidTransactionId(paymentData.transactionId)) {
            toast.error('Transaction ID contains invalid characters. Use only letters, numbers, hyphens, and underscores.');
            return;
        }

        if (!paymentData.paymentDate) {
            toast.error('Please select payment date');
            return;
        }

        // Security: Validate payment date (not in future)
        const selectedDate = new Date(paymentData.paymentDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate > today) {
            toast.error('Payment date cannot be in the future');
            return;
        }

        if (!proofImage) {
            toast.error('Please upload payment proof screenshot');
            return;
        }

        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('user_id', userId);
            formData.append('amount', amount || 4000);
            formData.append('pay_method', 'bank');
            
            // Security: Sanitize all inputs before sending
            formData.append('transaction_id', paymentData.transactionId.trim());
            formData.append('bank_name', (paymentData.bankName || bankDetails.bankName).substring(0, 100));
            formData.append('account_name', (paymentData.accountName || bankDetails.accountName).substring(0, 100));
            formData.append('account_number', (paymentData.accountNumber || bankDetails.accountNumber).substring(0, 50));
            formData.append('payment_date', paymentData.paymentDate);
            formData.append('notes', (paymentData.notes || '').substring(0, 500)); // Limit notes length
            formData.append('proof_image', proofImage);

            // Security: Add CSRF token if you have one
            // formData.append('csrf_token', getCsrfToken());

            const response = await axios.post('https://app.bihebari.com.np/api/payment/bank/initiate', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 30000 // 30 second timeout
            });

            if (response.data.success) {
                toast.success('Payment proof submitted successfully! Admin will verify and update status.');
                
                // Security: Clear sensitive data from memory
                setProofImage(null);
                if (imagePreview) {
                    URL.revokeObjectURL(imagePreview);
                    setImagePreview(null);
                }
                
                if (onSuccess) onSuccess();
                setTimeout(() => {
                    window.location.href = '/payment/success';
                }, 2000);
            } else {
                toast.error(response.data.message || 'Failed to submit payment proof');
            }
        } catch (error) {
            console.error('Bank transfer error:', error);
            
            // Security: Don't expose detailed error messages to client
            if (error.response?.status === 413) {
                toast.error('File too large. Please upload a smaller image.');
            } else if (error.response?.status === 415) {
                toast.error('Unsupported file type.');
            } else {
                toast.error(error.response?.data?.message || 'Failed to submit payment proof. Please try again.');
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 md:p-8">
                    {/* Header */}
                    <div className="flex items-center mb-6">
                        <button onClick={onBack} className="mr-4 text-gray-600 hover:text-gray-800">
                            <FaArrowLeft size={20} />
                        </button>
                        <h2 className="text-2xl font-bold text-gray-800">Bank Transfer Payment</h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Column - Bank Details (same as before) */}
                        <div className="space-y-6">
                            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                                <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center">
                                    <FaQrcode className="mr-2" />
                                    Bank Account Details
                                </h3>
                                
                                {/* QR Code */}
                                <div className="flex justify-center mb-6">
                                    {bankDetails.qrCodeUrl && bankDetails.qrCodeUrl !== 'https://example.com/qr-code.png' ? (
                                        <div className="text-center">
                                            <img 
                                                src={bankDetails.qrCodeUrl} 
                                                alt="Bank QR Code"
                                                className="w-48 h-48 object-contain border-2 border-gray-200 rounded-lg p-2 bg-white cursor-pointer"
                                                onClick={downloadQRCode}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="currentColor"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3Cpath d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"%3E%3C/path%3E%3C/svg%3E';
                                                }}
                                            />
                                            <button
                                                onClick={downloadQRCode}
                                                className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1"
                                            >
                                                <FaDownload size={12} />
                                                Download QR
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                                            <FaQrcode size={80} className="text-gray-400" />
                                        </div>
                                    )}
                                </div>

                                {/* Bank Details - Same as before */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center p-2 bg-white rounded">
                                        <span className="text-gray-600">Bank Name:</span>
                                        <span className="font-semibold">{bankDetails.bankName}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-white rounded">
                                        <span className="text-gray-600">Account Name:</span>
                                        <span className="font-semibold">{bankDetails.accountName}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-white rounded">
                                        <span className="text-gray-600">Account Number:</span>
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono font-semibold">{bankDetails.accountNumber}</span>
                                            <button 
                                                onClick={() => copyToClipboard(bankDetails.accountNumber, 'Account Number')}
                                                className="text-blue-600 hover:text-blue-800"
                                                title="Copy account number"
                                            >
                                                <FaCopy size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-white rounded">
                                        <span className="text-gray-600">Account Type:</span>
                                        <span>{bankDetails.accountType}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-white rounded">
                                        <span className="text-gray-600">Branch:</span>
                                        <span>{bankDetails.branch}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-white rounded">
                                        <span className="text-gray-600">SWIFT Code:</span>
                                        <span className="font-mono">{bankDetails.swiftCode}</span>
                                    </div>
                                </div>

                                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-sm text-yellow-800">
                                        <strong>Important:</strong> Please use your registered mobile number or User ID as reference while transferring.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Payment Proof Form */}
                        <div className="space-y-6">
                            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                    <FaUpload className="mr-2 text-green-600" />
                                    Submit Payment Proof
                                </h3>

                                <div className="space-y-4">
                                    {/* Amount Display */}
                                    <div className="p-3 bg-green-50 rounded-lg text-center border border-green-200">
                                        <span className="text-gray-600">Amount to Pay:</span>
                                        <span className="ml-2 text-2xl font-bold text-green-600">Rs. {amount || 4000}</span>
                                    </div>

                                    {/* Transaction ID */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Transaction ID / Reference Number *
                                        </label>
                                        <input
                                            type="text"
                                            name="transactionId"
                                            value={paymentData.transactionId}
                                            onChange={handleInputChange}
                                            placeholder="Enter transaction ID from bank"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                            maxLength="50"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Use only letters, numbers, hyphens, and underscores</p>
                                    </div>

                                    {/* Bank Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Your Bank Name (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            name="bankName"
                                            value={paymentData.bankName}
                                            onChange={handleInputChange}
                                            placeholder="Your bank name"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                            maxLength="100"
                                        />
                                    </div>

                                    {/* Payment Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Date *
                                        </label>
                                        <input
                                            type="date"
                                            name="paymentDate"
                                            value={paymentData.paymentDate}
                                            onChange={handleInputChange}
                                            max={new Date().toISOString().split('T')[0]}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>

                                    {/* Upload Proof */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Proof (Screenshot) *
                                        </label>
                                        <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition ${fileError ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-indigo-500'}`}>
                                            <div className="space-y-1 text-center">
                                                {imagePreview ? (
                                                    <div className="relative inline-block">
                                                        <img 
                                                            src={imagePreview} 
                                                            alt="Payment proof preview" 
                                                            className="mx-auto h-32 w-auto object-contain rounded border"
                                                        />
                                                        <button
                                                            onClick={() => {
                                                                setProofImage(null);
                                                                if (imagePreview) URL.revokeObjectURL(imagePreview);
                                                                setImagePreview(null);
                                                                setFileError('');
                                                            }}
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 text-xs hover:bg-red-600"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                                                        <div className="flex text-sm text-gray-600">
                                                            <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                                                                <span>Upload a file</span>
                                                                <input
                                                                    type="file"
                                                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                                                    onChange={handleFileChange}
                                                                    className="sr-only"
                                                                />
                                                            </label>
                                                            <p className="pl-1">or drag and drop</p>
                                                        </div>
                                                        <p className="text-xs text-gray-500">
                                                            JPEG, PNG, WEBP up to 2MB
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {fileError && (
                                            <p className="text-xs text-red-500 mt-1">{fileError}</p>
                                        )}
                                    </div>

                                    {/* Notes */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Additional Notes (Optional)
                                        </label>
                                        <textarea
                                            name="notes"
                                            value={paymentData.notes}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Any additional information..."
                                            maxLength="500"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            {paymentData.notes.length}/500 characters
                                        </p>
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        onClick={handleSubmit}
                                        disabled={uploading}
                                        className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center space-x-2 disabled:opacity-50"
                                    >
                                        {uploading ? (
                                            <>
                                                <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                                                <span>Submitting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FaCheckCircle />
                                                <span>Submit Payment Proof</span>
                                            </>
                                        )}
                                    </button>

                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <p className="text-sm text-blue-800 flex items-start">
                                            <FaRegClock className="mr-2 mt-0.5 flex-shrink-0" />
                                            <span>After submitting, admin will verify your payment within 24 hours. Your account will be activated upon verification.</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankTransferPayment;